import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  Truck,
  ClipboardList,
  ShoppingCart,
  PackageCheck,
  AlertTriangle,
} from 'lucide-react';
import { Heading } from '../layout';
import { useUIColors } from '../../hooks/useTheme';
import suppliersAPI from '../../services/suppliersAPI';
import purchaseRequestsAPI from '../../services/purchaseRequestsAPI';
import purchaseOrdersAPI from '../../services/purchaseOrdersAPI';
import purchaseReceiptsAPI from '../../services/purchaseReceiptsAPI';

const formatMoney = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const foldStatus = (s) =>
  String(s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');

const DashboardCard = ({ title, value, helper, icon, isDarkMode = false }) => (
  <div
    style={{
      border: `1px solid ${isDarkMode ? '#2f2f35' : '#e5e7eb'}`,
      borderRadius: '10px',
      padding: '14px',
      background: isDarkMode ? '#18181b' : '#ffffff',
      minHeight: '98px',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <p style={{ margin: 0, fontSize: '12px', color: isDarkMode ? '#a1a1aa' : '#6b7280' }}>{title}</p>
      <span style={{ color: isDarkMode ? '#d4d4d8' : '#374151' }}>{icon}</span>
    </div>
    <p style={{ margin: '8px 0 0', fontSize: '24px', fontWeight: 700, color: isDarkMode ? '#f4f4f5' : '#111827' }}>
      {value}
    </p>
    <p style={{ margin: '6px 0 0', fontSize: '12px', color: isDarkMode ? '#a1a1aa' : '#6b7280' }}>{helper}</p>
  </div>
);

const StatusBars = ({ title, rows, isDarkMode = false }) => {
  const max = rows.reduce((acc, row) => Math.max(acc, Number(row.value || 0)), 0) || 1;
  return (
    <div
      style={{
        border: `1px solid ${isDarkMode ? '#2f2f35' : '#e5e7eb'}`,
        borderRadius: '10px',
        padding: '12px',
        background: isDarkMode ? '#18181b' : '#ffffff',
      }}
    >
      <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: isDarkMode ? '#f4f4f5' : '#111827' }}>
        {title}
      </p>
      <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
        {rows.length === 0 ? (
          <p style={{ margin: 0, fontSize: 12, color: isDarkMode ? '#a1a1aa' : '#6b7280' }}>Sin datos</p>
        ) : (
          rows.map((row) => (
            <div key={row.label}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                <span style={{ color: isDarkMode ? '#d4d4d8' : '#374151' }}>{row.label}</span>
                <span style={{ color: isDarkMode ? '#a1a1aa' : '#6b7280', fontWeight: 600 }}>{row.value}</span>
              </div>
              <div
                style={{
                  height: 8,
                  borderRadius: 999,
                  background: isDarkMode ? '#27272a' : '#f3f4f6',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${Math.round((Number(row.value || 0) / max) * 100)}%`,
                    height: '100%',
                    background: row.color || '#2563eb',
                    borderRadius: 999,
                  }}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const countByStatus = (rows, colors = {}) => {
  const map = new Map();
  (rows || []).forEach((row) => {
    const label = String(row.status || 'Sin estado').trim() || 'Sin estado';
    map.set(label, (map.get(label) || 0) + 1);
  });
  return Array.from(map.entries())
    .map(([label, value]) => ({
      label,
      value,
      color: colors[foldStatus(label)] || '#64748b',
    }))
    .sort((a, b) => b.value - a.value);
};

const PaintProcurementSummaryWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const [suppliers, setSuppliers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [suppliersData, requestsData, ordersData, receiptsData] = await Promise.all([
          suppliersAPI.getAll(),
          purchaseRequestsAPI.getAll(),
          purchaseOrdersAPI.getAll(),
          purchaseReceiptsAPI.getAll().catch(() => []),
        ]);
        if (cancelled) return;
        setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
        setRequests(Array.isArray(requestsData) ? requestsData : []);
        setOrders(Array.isArray(ordersData) ? ordersData : []);
        setReceipts(Array.isArray(receiptsData) ? receiptsData : []);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError(err?.message || 'No se pudo cargar el resumen de adquisiciones.');
          setSuppliers([]);
          setRequests([]);
          setOrders([]);
          setReceipts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const metrics = useMemo(() => {
    const openRequestStatuses = new Set(['borrador', 'enviada', 'aprobada']);
    const openOrderStatuses = new Set(['borrador', 'enviada', 'confirmada', 'parcial']);
    const openRequests = requests.filter((r) => openRequestStatuses.has(foldStatus(r.status)));
    const openOrders = orders.filter((o) => openOrderStatuses.has(foldStatus(o.status)));
    const confirmedReceipts = receipts.filter((r) => foldStatus(r.status) === 'confirmada');
    const ordersAmount = orders.reduce((sum, o) => sum + Number(o.total_amount || 0), 0);
    const pendingReceive = orders.filter((o) =>
      ['enviada', 'confirmada', 'parcial'].includes(foldStatus(o.status))
    );
    return {
      suppliers: suppliers.length,
      openRequests: openRequests.length,
      openOrders: openOrders.length,
      ordersAmount,
      receipts: receipts.length,
      confirmedReceipts: confirmedReceipts.length,
      pendingReceive: pendingReceive.length,
    };
  }, [suppliers, requests, orders, receipts]);

  const requestStatusBars = useMemo(
    () =>
      countByStatus(requests, {
        borrador: '#a8a29e',
        enviada: '#3b82f6',
        aprobada: '#16a34a',
        rechazada: '#dc2626',
        'convertida a oc': '#0d9488',
      }),
    [requests]
  );

  const orderStatusBars = useMemo(
    () =>
      countByStatus(orders, {
        borrador: '#a8a29e',
        enviada: '#3b82f6',
        confirmada: '#2563eb',
        parcial: '#d97706',
        recibida: '#16a34a',
        anulada: '#dc2626',
      }),
    [orders]
  );

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: 6 }}>
          Resumen
        </Heading>
        <p style={{ margin: 0, maxWidth: 720, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>
          Vista general del pipeline de adquisiciones: proveedores, solicitudes, órdenes de compra y recepciones.
        </p>
      </div>

      {error ? (
        <div
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 8,
            border: `1px solid ${isDarkMode ? '#7f1d1d' : '#fecaca'}`,
            background: isDarkMode ? '#450a0a' : '#fef2f2',
            color: isDarkMode ? '#fca5a5' : '#b91c1c',
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <AlertTriangle size={16} /> {error}
        </div>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <DashboardCard
          title="Proveedores"
          value={loading ? '…' : metrics.suppliers}
          helper="Activos en catálogo"
          icon={<Truck size={18} />}
          isDarkMode={isDarkMode}
        />
        <DashboardCard
          title="Solicitudes abiertas"
          value={loading ? '…' : metrics.openRequests}
          helper={`${requests.length} totales`}
          icon={<ClipboardList size={18} />}
          isDarkMode={isDarkMode}
        />
        <DashboardCard
          title="OC abiertas"
          value={loading ? '…' : metrics.openOrders}
          helper={`${formatMoney(metrics.ordersAmount)} en OC`}
          icon={<ShoppingCart size={18} />}
          isDarkMode={isDarkMode}
        />
        <DashboardCard
          title="Recepciones"
          value={loading ? '…' : metrics.receipts}
          helper={`${metrics.confirmedReceipts} confirmadas · ${metrics.pendingReceive} OC por recibir`}
          icon={<PackageCheck size={18} />}
          isDarkMode={isDarkMode}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        <StatusBars title="Solicitudes por estado" rows={loading ? [] : requestStatusBars} isDarkMode={isDarkMode} />
        <StatusBars title="Órdenes de compra por estado" rows={loading ? [] : orderStatusBars} isDarkMode={isDarkMode} />
        <div
          style={{
            border: `1px solid ${isDarkMode ? '#2f2f35' : '#e5e7eb'}`,
            borderRadius: '10px',
            padding: '12px',
            background: isDarkMode ? '#18181b' : '#ffffff',
          }}
        >
          <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: isDarkMode ? '#f4f4f5' : '#111827' }}>
            Flujo
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 12, color: isDarkMode ? '#a1a1aa' : '#6b7280', lineHeight: 1.5 }}>
            Solicitud → Orden de compra → Recepción. Las recepciones confirmadas actualizan la OC a Recepción
            Conforme.
          </p>
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, color: isDarkMode ? '#d4d4d8' : '#374151' }}>
            <BarChart3 size={16} />
            <span style={{ fontSize: 12 }}>
              {loading ? 'Cargando…' : `${suppliers.length + requests.length + orders.length + receipts.length} registros`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaintProcurementSummaryWorkspace;
